/**
 * AW-1 -- the canonical MissionAssist product vocabulary for the
 * Agent Workspace.
 *
 * WHY THIS IS A MODULE AND NOT INLINE STRINGS
 * -------------------------------------------
 * These labels are the product's own state names, not decoration. The
 * shell previously rendered provider vocabulary ("Logged In - Paused")
 * for a product state the rest of the codebase already called Product
 * Ready in ten separate comments. Naming them once, here, is what stops
 * the two drifting apart again.
 *
 * PRODUCT READY IS NATIVELY PAUSED, AND THAT IS NOT A CONTRADICTION
 * -----------------------------------------------------------------
 * `PRESENCE_STATE.READY` and `PRESENCE_STATE.PAUSED` BOTH correspond to
 * a natively paused VICIdial agent. The difference is entirely
 * MissionAssist's: Product Ready means campaign login completed and
 * Start Dialing has not been confirmed; Product Paused means an agent
 * who was already dialing chose to stop. They offer different actions
 * (Start Dialing vs Resume) and have different remedies, so they must
 * never collapse into one label.
 *
 * This module changes ONLY what the agent reads. It holds no state,
 * touches no provider vocabulary, and must never be imported by
 * anything that decides a transition.
 */

export const PRODUCT_READY_LABEL = "Product Ready";
/** Campaign login is complete; Start Dialing has not been confirmed. */

export const PRODUCT_PAUSED_LABEL = "Paused";
/** An agent who was dialing chose to stop. Deliberately distinct from
 * PRODUCT_READY_LABEL -- see the module docstring. */

export const ACTIVE_WAITING_LABEL = "Active / Waiting";
/** Dialing has started and the agent is eligible for assignment.
 *
 * There is no Dialing and no Ringing label, here or anywhere: neither
 * is a state native's outbound RATIO lifecycle ever writes for an
 * agent, so neither can be shown without inventing something the
 * provider never said. ACQUIRING folds into this same label. */

export const ACTIVE_UNVERIFIED_LABEL = "Active";
/** Active, with the waiting claim deliberately withheld.
 *
 * AW-1 H5 containment. After `CALL_PHASE.ENDED` the backend has told us
 * a call ended without a verified native release, so MissionAssist does
 * NOT know that the provider re-established an assignable state. Saying
 * "Waiting" there would assert the one thing in doubt -- that another
 * call can still arrive. The bare label claims only what is known: the
 * session is still Active.
 *
 * This is presentation containment, not recovery. AW-1 implements no
 * resume and changes no backend presence behavior. */

export const STRANDED_RETAINED_LEAD_LABEL = "Session ended - lead unresolved";
/** AW-2B3 -- the owned call ended, the provider session is gone, and the
 * native lead the call claimed was never completed.
 *
 * Deliberately not "Call assignment failed": a call *was* assigned and
 * did connect. Deliberately not any Active, Ready, Paused or Wrap-up
 * label either -- the first three claim a session that no longer
 * exists, and the last claims a disposition the backend has proven is
 * unreachable.
 *
 * "unresolved" is chosen over "incomplete" or "pending" because it
 * claims only what is known: nothing asserts here that the lead was
 * dispositioned, recovered, released or queued for anything. */

export const STRANDED_RETAINED_LEAD_NOTICE =
  "This call is over and the dialing session has ended. The lead was not " +
  "completed and needs supervisor follow-up -- it cannot be completed " +
  "here. Log out of the campaign when you are ready.";
/** AW-2B3 -- what the agent is told, and the four things it must not say.
 *
 * It must not offer or imply a disposition: the backend has no session
 * through which one could be sent, so inviting the attempt would send
 * the agent at a control that cannot exist.
 *
 * It must not say logging out and back in recovers the lead. It does
 * not. A new login opens a new session and leaves this lead exactly as
 * it is, which is why `ACQUISITION_FAILED`'s "log back in to recover"
 * copy is wrong here even though the shape of the state is similar.
 *
 * It must not say the lead was dispositioned, resolved or released --
 * the one claim the evidence never supports.
 *
 * It must still name an action, because a state with no exit is its own
 * defect. Campaign logout is the only relevant one, and native honours
 * `userLOGout` unconditionally. */

/**
 * Which Active label to render.
 *
 * `waitingConfirmed` must be false whenever an ENDED phase has been
 * observed and nothing since has re-established assignability. It is
 * deliberately a required decision rather than an optional flag with a
 * permissive default: defaulting to the confident label is exactly the
 * unsupported claim this exists to prevent.
 */
export function activeStatusLabel({ waitingConfirmed } = {}) {
  return waitingConfirmed ? ACTIVE_WAITING_LABEL : ACTIVE_UNVERIFIED_LABEL;
}
